/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import {
  ICommunityCategory,
  ICommunityQuestion,
} from '@sv-support-sdk/shared-types/src/common/community';
import {
  COMMUNITY_ANSWERS_SORT,
  COMMUNITY_SORT,
  SORT_ANSWERS_LIST,
  SORT_LIST,
} from '@sv-support-sdk/utils/src/constants/community';
import { reverseDirection } from '../../helpers/community';

interface IFiltersCommunity {
  perPage: number;
  page: number;
  total: number;
  sort: string;
  direction: string;
}

interface ICommunityState {
  categories: ICommunityCategory[];
  questions: ICommunityQuestion[];
  listFilter: IFiltersCommunity;
  communityUser: {
    id?: number | null;
    firstName?: string;
    lastName?: string;
  };
  answersSort: string;
  loading: { [key: string]: boolean };
}

const initFilters = {
  perPage: 15,
  page: 1,
  total: 0,
  sort: localStorage.getItem(COMMUNITY_SORT) || SORT_LIST[0].value,
  direction: 'desc',
};

const initialState = {
  categories: [],
  questions: [],
  listFilter: initFilters,
  communityUser: {
    id: null,
    firstName: '',
    lastName: '',
  },
  answersSort: localStorage.getItem(COMMUNITY_ANSWERS_SORT) || SORT_ANSWERS_LIST[0].value,
  loading: {
    communityUser: false,
    categories: false,
    questions: false,
    myQuestions: false,
    allQuestions: false,
    question: false,
  },
} as ICommunityState;

export const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    setCategories: (state: ICommunityState, action) => {
      state.categories = action.payload;
    },
    setQuestions: (state: ICommunityState, action) => {
      state.questions = action.payload;
    },
    setQuestion: (state: ICommunityState, action) => {
      const questionIndex = state.questions.findIndex(
        (question) => question.uuid === action.payload.uuid,
      );

      if (questionIndex < 0) {
        state.questions.unshift(action.payload);
      } else {
        state.questions[questionIndex] = action.payload;
      }
    },
    addAnswer: (state: ICommunityState, action) => {
      const question = state.questions.find((el) => el.uuid === action.payload.questionUuid);

      if (!question) return;

      if (action.payload.parentUuid) {
        const parentAnswer = question.answers?.find(
          (answer) => answer.uuid === action.payload.parentUuid,
        );

        if (!parentAnswer) return;

        const subAnswerIndex = parentAnswer?.subAnswers.findIndex(
          (subAnswer) => subAnswer.uuid === action.payload.parentUuid,
        );

        if (!subAnswerIndex) return;

        if (subAnswerIndex >= 0) {
          parentAnswer.subAnswers[subAnswerIndex] = {
            ...parentAnswer.subAnswers[subAnswerIndex],
            ...action.payload,
          };
        } else {
          parentAnswer?.subAnswers.push(action.payload);
        }
      } else {
        if (!question.answers) {
          question.answers = [action.payload];
        } else {
          const answerIndex = question.answers?.findIndex(
            (answer) => answer.uuid === action.payload.uuid,
          );

          if (answerIndex >= 0) {
            question.answers[answerIndex] = {
              ...question.answers[answerIndex],
              ...action.payload,
            };
          } else {
            question.answers?.push(action.payload);
          }
        }
      }
    },
    addRatedQuestion: (state: ICommunityState, action) => {
      const ratedQuestion = state.questions.find(
        (question) => question.uuid === action.payload.uuid,
      );

      if (ratedQuestion) {
        ratedQuestion.rating += action.payload.like;
      }
    },
    addRatedAnswer: (state: ICommunityState, action) => {
      const ratedQuestion = state.questions.find(
        (question) => question.uuid === action.payload.questionUuid,
      );

      if (ratedQuestion) {
        const ratedAnswer = ratedQuestion.answers?.find(
          (answer) => answer.uuid === action.payload.uuid,
        );

        if (ratedAnswer) {
          ratedAnswer.rating += action.payload.like;
        }
      }
    },
    setFilters: (state: ICommunityState, action) => {
      state.listFilter = {
        ...state.listFilter,
        page: 1,
        direction:
          action.payload.sort === state.listFilter.sort
            ? reverseDirection(state.listFilter.direction)
            : 'desc',
        ...action.payload,
      };
    },
    setCommunityUser: (state: ICommunityState, action) => {
      state.communityUser = action.payload;
    },
    resetCommunityUser: (state: ICommunityState) => {
      state.communityUser = {
        id: null,
        firstName: '',
        lastName: '',
      };
    },
    setAnswersSort: (state: ICommunityState, action) => {
      state.answersSort = action.payload;
    },
    setLoading: (state: ICommunityState, action) => {
      state.loading[action.payload.key] = action.payload.value;
    },
  },
});

export const {
  setCategories,
  setQuestions,
  setQuestion,
  addAnswer,
  addRatedQuestion,
  addRatedAnswer,
  setFilters,
  setCommunityUser,
  resetCommunityUser,
  setAnswersSort,
  setLoading,
} = communitySlice.actions;

export default communitySlice.reducer;
